/**
 * Departments as sections in the week (#199, prototype 2026-10-26): the rows under a heading per
 * department, with its hours and labour cost per day against its share of the budget.
 *
 * A department is the person's team (Ansatte, Team): the only grouping of people a company keeps
 * here. PowerOffice and Tripletex know one department per store, not per section, and a shift type
 * has none. A person in two teams goes under the first by name. Pure; tested in departments.test.ts.
 */
import type { ApiShift } from '@/api/data';
import { plannedHoursOn } from '@/rules';
import { type Row, dateOf } from './model';

export interface Section {
  /** the team id, or '' for the people in none */
  key: string;
  name: string;
  rows: Row[];
}

const teamOf = (r: Row) =>
  [...(r.user.teams ?? [])].filter((t) => t.name).sort((a, b) => a.name.localeCompare(b.name, 'nb'))[0];

/** The rows by team, teams by name and the people in none last; null when nobody on screen has a team. */
export function sectionsOf(rows: Row[], none: string): Section[] | null {
  if (!rows.some(teamOf)) return null;
  const by = new Map<string, Section>();
  for (const r of rows) {
    const t = teamOf(r);
    const key = t ? String(t.id) : '';
    const s = by.get(key) ?? { key, name: t?.name ?? none, rows: [] };
    s.rows.push(r);
    by.set(key, s);
  }
  return [...by.values()].sort(
    (a, b) => Number(a.key === '') - Number(b.key === '') || a.name.localeCompare(b.name, 'nb'),
  );
}

export interface SectionDay {
  hours: number;
  cost: number;
  /** the store's budget that day times the department's share of the week's cost; null without one */
  budget: number | null;
}

/**
 * A department's days. Hours by the Sum row's rule (plannedHoursOn, a night's part on each date), cost by
 * the page's own model on the shifts that start that day. The budget is the store's for the day split by
 * the department's share of the week's cost, as the prototype does (#199: the split is not decided).
 */
export function sectionDays(
  section: Section,
  days: string[],
  shifts: ApiShift[],
  cost: (list: ApiShift[]) => number,
  storeBudget: (day: string) => number | null,
): { days: Map<string, SectionDay>; hours: number } {
  const ids = new Set(section.rows.map((r) => r.user.id));
  const assigned = shifts.filter((s) => s.user_id != null && days.includes(dateOf(s)));
  const mine = assigned.filter((s) => ids.has(s.user_id as number));
  const all = cost(assigned);
  const share = all > 0 ? cost(mine) / all : 0;
  const out = new Map<string, SectionDay>();
  let hours = 0;
  for (const d of days) {
    const h = shifts
      .filter((s) => s.user_id != null && ids.has(s.user_id))
      .reduce((a, s) => a + plannedHoursOn(s, d), 0);
    hours += h;
    const b = storeBudget(d);
    out.set(d, {
      hours: h,
      cost: cost(mine.filter((s) => dateOf(s) === d)),
      budget: b == null ? null : b * share,
    });
  }
  return { days: out, hours };
}
